import { useState, useEffect } from "react";
import { 
  History as HistoryIcon, 
  CheckCircle2, 
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import api from "@/services/api";
import { format, formatDistanceToNow } from "date-fns";

const StatusBadge = ({ status }) => {
  const config = {
    completed: { icon: CheckCircle2, label: "Completed", className: "bg-green-100 text-green-700 border-green-200" },
    failed: { icon: AlertCircle, label: "Failed", className: "bg-red-100 text-red-700 border-red-200" },
    running: { icon: Clock, label: "Running", className: "bg-blue-100 text-blue-700 border-blue-200" },
  };
  
  const { icon: Icon, label, className } = config[status] || config.running;
  
  return (
    <Badge variant="outline" className={className}>
      <Icon className={`w-3 h-3 mr-1 ${status === "running" ? "animate-spin" : ""}`} />
      {label}
    </Badge>
  );
};

const ExecutionCard = ({ execution }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const duration = execution.completed_at 
    ? Math.round((new Date(execution.completed_at) - new Date(execution.started_at)) / 1000)
    : null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="shadow-soft" data-testid={`execution-${execution.id}`}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  execution.status === "completed" ? "bg-green-50" :
                  execution.status === "failed" ? "bg-red-50" : "bg-blue-50"
                }`}>
                  {execution.status === "completed" ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : execution.status === "failed" ? (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold">{execution.pipeline_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(execution.started_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <StatusBadge status={execution.status} />
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {/* Execution Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Started</p>
                <p className="text-sm font-medium mt-1">
                  {format(new Date(execution.started_at), "PPp")}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Completed</p>
                <p className="text-sm font-medium mt-1">
                  {execution.completed_at 
                    ? format(new Date(execution.completed_at), "PPp")
                    : "In progress..."
                  }
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Duration</p>
                <p className="text-sm font-medium mt-1">
                  {duration !== null ? `${duration}s` : "Running..."}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Rows Processed</p>
                <p className="text-sm font-medium mt-1">
                  {execution.rows_processed.toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Error Message */}
            {execution.error_message && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-700">Error</p>
                <p className="text-sm text-red-600 mt-1 font-mono">
                  {execution.error_message}
                </p>
              </div>
            )}
            
            {/* Stage Logs */}
            {execution.stage_logs && execution.stage_logs.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Stage Logs</p>
                <div className="space-y-2">
                  {execution.stage_logs.map((log, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${
                        log.status === "completed" ? "bg-green-50 border-green-200" :
                        log.status === "failed" ? "bg-red-50 border-red-200" : 
                        "bg-blue-50 border-blue-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className={`capitalize ${
                          log.stage_type === "bronze" ? "badge-bronze" :
                          log.stage_type === "silver" ? "badge-silver" : "badge-gold"
                        }`}>
                          {log.stage_type}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {log.target_table}
                        </span>
                      </div>
                      <p className="text-sm mt-2">
                        Rows: {log.rows_processed?.toLocaleString() || 0}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default function History() {
  const [executions, setExecutions] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPipeline, setSelectedPipeline] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [executionsData, pipelinesData] = await Promise.all([
        api.getExecutions(),
        api.getPipelines()
      ]);
      setExecutions(executionsData);
      setPipelines(pipelinesData);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const filteredExecutions = selectedPipeline === "all"
    ? executions
    : executions.filter(e => e.pipeline_id === selectedPipeline);

  // Stats
  const totalRuns = executions.length;
  const successfulRuns = executions.filter(e => e.status === "completed").length;
  const failedRuns = executions.filter(e => e.status === "failed").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="history-page">
      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-soft">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <HistoryIcon className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalRuns}</p>
              <p className="text-sm text-muted-foreground">Total Runs</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{successfulRuns}</p>
              <p className="text-sm text-muted-foreground">Successful</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-soft">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{failedRuns}</p>
              <p className="text-sm text-muted-foreground">Failed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter & Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={selectedPipeline} onValueChange={setSelectedPipeline}>
            <SelectTrigger className="w-[200px]" data-testid="pipeline-filter">
              <SelectValue placeholder="Filter by pipeline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pipelines</SelectItem>
              {pipelines.map((pipeline) => (
                <SelectItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Button variant="outline" onClick={fetchData} data-testid="refresh-history">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Executions List */}
      {filteredExecutions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="empty-state">
            <HistoryIcon className="empty-state-icon" />
            <h3 className="font-semibold text-lg">No Execution History</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-sm">
              {selectedPipeline !== "all" 
                ? "No runs found for the selected pipeline"
                : "Run a pipeline to see execution history here"
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredExecutions.map((execution) => (
            <ExecutionCard key={execution.id} execution={execution} />
          ))}
        </div>
      )}
    </div>
  );
}
